import React, { useEffect, useState, useMemo } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import style from './AgentsTable.module.scss';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Check, Clear } from '@mui/icons-material';
import { Eye } from 'lucide-react';
import Button from 'tailwind-components/Button';
import { getViewAsDisabledReason } from 'helpers/viewAsAgentLink';
import { isMobileWebView, postMobileViewAsAgent } from 'helpers/mobileWebView';

function AgentsTable({ agents }) {
  const [tableData, setTableData] = useState({ pageSize: 0, data: [] });
  const { zohoData, mainZohoId } = useSelector(state => state.user);

  useEffect(() => {
    const pageSize = Math.min(100, agents.length);
    setTableData({
      pageSize,
      data: [...agents]
    });
  }, [agents]);

  const handleViewAsAgent = agentID => {
    if (isMobileWebView()) {
      postMobileViewAsAgent(agentID);
      return;
    }
    window.open(`/home?va=${agentID}`, '_blank');
  };

  const headerStyle = name => (
    <div
      style={{
        size: '5px',
        height: '48px',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {name}
    </div>
  );
  const headerStyleCentered = name => (
    <div
      style={{
        size: '5px',
        height: '48px',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {name}
    </div>
  );
  const columns = useMemo(
    () => [
      {
        header: 'View as agent',
        accessorKey: 'viewasagent',
        grow: false,
        size: 109,
        Cell: rowProps => {
          const target = rowProps.row.original;
          const isSelfTarget =
            target.id === mainZohoId || target.id === zohoData?.id;
          const disabledReason = getViewAsDisabledReason({
            requesterZohoData: zohoData,
            isSelfTarget
          });
          return (
            <span title={disabledReason || undefined}>
              <Button
                disabled={Boolean(disabledReason)}
                size='xsmall'
                variant='secondary'
                text='View'
                icon={<Eye />}
                onClick={() => handleViewAsAgent(target.id)}
              />
            </span>
          );
        },
        Header: headerStyle('View as agent')
      },
      {
        header: 'Name',
        accessorKey: 'First_Name',
        grow: false,
        size: 220,
        Cell: rowProps =>
          cellContainer(
            rowProps.row.original.First_Name +
              ' ' +
              rowProps.row.original.Last_Name
          ),
        Header: headerStyle('Name')
      },
      {
        header: 'NPN',
        accessorKey: 'NPN',
        grow: false,
        size: 100,
        Cell: rowProps => cellContainer(rowProps.row.original.NPN),
        Header: headerStyle('NPN')
      },
      {
        header: 'Phone',
        accessorKey: 'Phone',
        grow: false,
        size: 100,
        Cell: rowProps => cellContainer(rowProps.row.original.Phone),
        Header: headerStyle('Phone')
      },
      {
        header: 'Email',
        accessorKey: 'Email',
        grow: false,
        size: 100,
        Cell: rowProps => cellContainer(rowProps.row.original.Email),
        Header: headerStyle('Email')
      },
      {
        header: 'Agency',
        accessorKey: 'Agency',
        grow: false,
        size: 200,
        Cell: rowProps => cellContainer(rowProps.row.original.Agency),
        Header: headerStyle('Agency')
      },
      {
        header: 'Position',
        accessorKey: 'Position_at_Agency',
        grow: false,
        size: 100,
        Cell: rowProps =>
          cellContainer(rowProps.row.original.Position_at_Agency || ''),
        Header: headerStyle('Position')
      },
      {
        header: 'Onboarding Completed?',
        accessorKey: 'Initial_Onboarding_Form_Completed',
        grow: false,
        size: 100,
        Cell: rowProps =>
          cellContainerCheckMark(
            rowProps.row.original.Initial_Onboarding_Form_Completed
          ),
        Header: headerStyle('Onboarding Completed?')
      },
      {
        header: 'Active',
        accessorKey: 'Active',
        grow: false,
        size: 100,
        Cell: rowProps => {
          const isActive = rowProps.row.original.rank !== 'Inactive';
          return cellContainerCheckMark(isActive);
        },
        Header: headerStyleCentered('Active')
      }
    ],
    [mainZohoId, zohoData]
  );

  const dataForTable = React.useMemo(
    () => [...tableData.data],
    [tableData.data]
  );
  const table = useMaterialReactTable({
    columns,
    data: dataForTable,
    enableSorting: false,
    enableColumnActions: false,
    // search lives in the shared AgentsFiltersToolbar above the table
    enableGlobalFilter: false,
    initialState: {
      pagination: {
        pageSize: tableData.pageSize > 0 ? tableData.pageSize : 100 // Default page size 100
      }
    },
    muiTableHeadCellProps: ({ column }) => {
      if (column.columnDef.accessorKey === 'Active') {
        return {
          align: 'center',
          sx: {
            '& .Mui-TableHeadCell-Content': {
              justifyContent: 'center'
            }
          }
        };
      }
      return {};
    },
    muiTableBodyCellProps: ({ column }) => {
      if (column.columnDef.accessorKey === 'Active') {
        return {
          align: 'center'
        };
      }
      return {};
    },
    renderEmptyRowsFallback: () => (
      <h2
        style={{
          color: 'grey',
          textAlign: 'center',
          fontWeight: '400',
          fontSize: 14
        }}
      >
        No agents related to this agency
      </h2>
    )
  });

  return <MaterialReactTable table={table} />;
}

AgentsTable.propTypes = {
  agents: propTypes.array
};

export default AgentsTable;

const cellContainer = text => {
  return <span className={style.cellContainer}>{text}</span>;
};

const cellContainerCheckMark = completed => {
  return (
    <div className={style.checkMark}>
      {completed ? <Check color={'success'} /> : <Clear color='warning' />}
    </div>
  );
};
